import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@datapay-by-datacom/design-system";
import { VersionRow } from "../components";
import { useCompanyConfigChangeLog } from "../hooks";
import s from "./CompanyConfigVersionsScreen.module.css";

interface CompanyConfigVersionsScreenProps {
  presentation?: "legacy" | "redesign";
  configurationBasePath?: string;
}

/**
 * Full version history for a single company configuration
 * (`/onboarding/:configCode/versions`). Lists every version newest-first,
 * PUBLISHED and RETIRED alike, each with a status badge. Published versions
 * can be retired from here.
 */
export function CompanyConfigVersionsScreen({
  presentation = "redesign",
  configurationBasePath = "/onboarding",
}: CompanyConfigVersionsScreenProps) {
  const navigate = useNavigate();
  const { configCode } = useParams<{ configCode: string }>();
  const { data, isLoading, isError } = useCompanyConfigChangeLog(configCode);

  // Backend returns ascending; show newest version first. Computed
  // unconditionally (before the early return below) so this hook is always
  // called in the same order, per the rules of hooks.
  const versions = useMemo(
    () => (data ? [...data].sort((a, b) => b.version - a.version) : []),
    [data]
  );

  // A missing :configCode means the route was reached with a malformed URL.
  // Render an error rather than showing "undefined" and building links like
  // /onboarding/undefined/versions/1/retire.
  if (!configCode) {
    return (
      <div className={s.container}>
        <div className={s.error} role="alert">
          No company configuration was specified.
        </div>
      </div>
    );
  }

  const displayName = versions[0]?.display_name;
  const isRedesign = presentation === "redesign";
  const configurationPath = `${configurationBasePath}/${configCode}`;

  return (
    <div
      className={`${s.container} ${
        isRedesign ? s.redesignContainer : ""
      }`}
    >
      {isRedesign && (
        <Link to={configurationPath} className={s.backLink}>
          &larr; Back to configuration
        </Link>
      )}

      <header
        className={`${s.header} ${isRedesign ? s.redesignHeader : ""}`}
      >
        <div className={s.headerText}>
          <h1 className={s.title}>Version history</h1>
          <p className={s.subtitle}>
            {configCode}
            {displayName ? ` · ${displayName}` : ""}
          </p>
        </div>
        {isRedesign ? (
          data && (
            <div className={s.summaryMeta} aria-label="History summary">
              <span className={s.summaryCode}>{configCode}</span>
              <span>
                {versions.length} version{versions.length === 1 ? "" : "s"}
              </span>
            </div>
          )
        ) : (
          <Button
            variant="tertiary"
            onPress={() => navigate(configurationPath)}
          >
            Back to configuration
          </Button>
        )}
      </header>

      {isLoading && (
        <p className={s.status} role="status">
          Loading version history…
        </p>
      )}
      {isError && (
        <div className={s.error} role="alert">
          Could not load the version history for &quot;{configCode}&quot;.
        </div>
      )}

      {data && versions.length === 0 && (
        <p className={s.empty}>This company configuration has no versions.</p>
      )}

      {versions.length > 0 && (
        <ul className={s.list}>
          {versions.map((version) => (
            <VersionRow
              key={version.version}
              version={version}
              presentation={presentation}
              onRetire={() =>
                navigate(
                  `${configurationBasePath}/${configCode}/versions/${version.version}/retire`
                )
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}
